import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { apiPost } from '../services/api';

interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  avatarUrl?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, firstName: string, lastName?: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const isLoggingOutRef = useRef(false); // Prevent multiple simultaneous logout calls

  // Logout function - needs to be defined early for use in other effects
  const logout = useCallback(async () => {
    // ✅ Prevent multiple simultaneous logout calls
    if (isLoggingOutRef.current) {
      console.warn('Logout already in progress, skipping...');
      return;
    }

    isLoggingOutRef.current = true;

    try {
      await apiPost('/auth/logout', {});
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setToken(null);
      localStorage.removeItem('oracle_auth_token');
      localStorage.removeItem('oracle_user');
      localStorage.removeItem('oracle_login_timestamp'); // Clean up old timestamp if exists
      isLoggingOutRef.current = false;
    }
  }, []);

  // Initialize from localStorage and validate session
  useEffect(() => {
    const initAuth = async () => {
      const storedUser = localStorage.getItem('oracle_user');
      const storedToken = localStorage.getItem('oracle_auth_token');

      if (storedUser && storedToken) {
        try {
          // Restore user from localStorage immediately (optimistic)
          const userData = JSON.parse(storedUser);
          setUser(userData);
          setToken(storedToken);
          
          // Then validate with backend (WordPress session) in background
          try {
            const response = await apiPost('/auth/validate', {});
            
            if (response.success && response.authenticated) {
              // Update with fresh user data from server
              setUser(response.user);
              setToken(storedToken);
            } else {
              // Session expired on server, but keep user data for now (might be cookie issue)
              // Only clear if we're sure the user doesn't exist
              console.warn('Session validation returned false, but keeping user data');
            }
          } catch (validateErr) {
            // Validation failed, but don't clear - might be network issue
            // Keep the user logged in from localStorage
            console.warn('Session validation request failed, keeping cached user:', validateErr);
          }
        } catch (err) {
          console.error('Failed to parse stored user:', err);
          localStorage.removeItem('oracle_user');
          localStorage.removeItem('oracle_auth_token');
          setUser(null);
          setToken(null);
        }
      }

      setLoading(false);
    };

    initAuth();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiPost('/auth/login', { email, password });

      if (response.success) {
        setToken(response.token);
        setUser(response.user);

        // Store in localStorage
        localStorage.setItem('oracle_auth_token', response.token);
        localStorage.setItem('oracle_user', JSON.stringify(response.user));
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Login failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (
    email: string,
    password: string,
    firstName: string,
    lastName?: string
  ) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiPost('/auth/register', {
        email,
        password,
        firstName,
        lastName,
      });

      if (response.success) {
        setToken(response.token);
        setUser(response.user);

        // Store in localStorage
        localStorage.setItem('oracle_auth_token', response.token);
        localStorage.setItem('oracle_user', JSON.stringify(response.user));
      } else {
        throw new Error(response.message || 'Registration failed');
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Registration failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = {
    user,
    token,
    isAuthenticated: !!user,
    loading,
    error,
    login,
    register,
    logout,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
